import * as monaco from 'monaco-editor'
import { useEffect, useRef, useState } from 'react'
import { ErrorBlock } from '~/components/ErrorBlock'
import { cm } from '~/utils/utils'
import { getMonacoOptions, initializeMonaco } from './monacoInitializer'
import { ValidationResult, validationStrategies } from './validationStrategies'

type Language = 'xml' | 'json' | 'csv' | 'yaml' | 'edi' | 'auto'

interface MonacoTextAreaProps {
  value: string
  onChange: (value: string) => void
  language?: Language
  theme?: string
  options?: monaco.editor.IEditorOptions
  placeholder?: string
  className?: string
  requireLangs?: Language[]
  onError: () => void
  onErrorClear: () => void
  isReadOnly?: boolean
}

await initializeMonaco()

export const MonacoTextEditor = ({
  value,
  onChange,
  language = 'auto',
  theme = 'vs-dark',
  options = {},
  placeholder = '',
  className,
  onError,
  onErrorClear,
  requireLangs = [],
  isReadOnly = false
}: MonacoTextAreaProps) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const decorationIds = useRef<string[]>([])
  const errorDecorations = useRef<string[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const [detectedLanguage, setDetectedLanguage] = useState<string>(language)
  const languageRef = useRef(language)
  const requireLangsRef = useRef(requireLangs)

  useEffect(() => {
    requireLangsRef.current = requireLangs
  }, [requireLangs])

  useEffect(() => {
    if (containerRef.current) {
      editorRef.current = monaco.editor.create(containerRef.current, {
        value: value,
        theme: theme,
        automaticLayout: true,
        minimap: { enabled: false },
        ...getMonacoOptions(),
        ...options,
        language: detectedLanguage,
        readOnly: isReadOnly
      })

      editorRef.current.onDidChangeModelContent(() => {
        const newValue = editorRef.current!.getValue()
        onChange(newValue)
        updatePlaceholder(newValue)
        validateContent(newValue, languageRef.current)
      })

      updatePlaceholder(value)
      validateContent(value, languageRef.current)
    }

    return () => {
      editorRef.current?.dispose()
    }
  }, [])

  useEffect(() => {
    if (editorRef.current) {
      const model = editorRef.current.getModel()
      if (model && value !== model.getValue()) {
        model.setValue(value)
        updatePlaceholder(value)
        validateContent(value, languageRef.current)
      }
    }
  }, [value])

  useEffect(() => {
    languageRef.current = language
    if (editorRef.current) {
      if (language === 'auto') {
        detectLanguage(editorRef.current.getValue())
      } else {
        setDetectedLanguage(language)
        monaco.editor.setModelLanguage(editorRef.current.getModel()!, language)
        validateContent(editorRef.current.getValue(), language)
      }
    }
  }, [language])

  const detectLanguage = (content: string) => {
    // If requireLangs is provided, only check those languages
    const languagesToCheck =
      requireLangsRef.current.length > 0
        ? requireLangsRef.current
        : (['xml', 'json', 'yaml', 'csv'] as const)

    for (const lang of languagesToCheck) {
      const result = validationStrategies[lang].validate(content)
      if (result.isValid) {
        setDetectedLanguage(lang)
        if (editorRef.current) {
          monaco.editor.setModelLanguage(editorRef.current.getModel()!, lang)
        }
        validateContent(content, lang)
        onErrorClear()
        return
      }
    }

    // If we have required languages and none matched, show a specific error
    if (requireLangsRef.current.length > 0) {
      const requiredFormats = requireLangsRef.current.map(lang => lang.toUpperCase()).join(', ')
      setValidationError(`Invalid format. Content must be valid: ${requiredFormats}.`)
      onError()
    } else {
      setValidationError('Invalid format. Must be one of: XML, JSON, YAML, or CSV.')
      onError()
    }

    setDetectedLanguage('plaintext') // Default to plaintext if no valid format is detected
    if (editorRef.current) {
      monaco.editor.setModelLanguage(editorRef.current.getModel()!, 'plaintext')
    }
  }

  const updatePlaceholder = (currentValue: string) => {
    if (!editorRef.current || !placeholder) return

    const model = editorRef.current.getModel()
    if (!model) return

    editorRef.current.deltaDecorations(decorationIds.current, [])

    if (currentValue.trim() === '') {
      const decorations = [
        {
          range: new monaco.Range(1, 1, 1, 1),
          options: {
            isWholeLine: true,
            className: 'monaco-placeholder',
            glyphMarginClassName: 'monaco-placeholder',
            overviewRuler: { color: 'transparent', position: 1 },
            minimap: { color: 'transparent', position: 1 },
            after: {
              content: placeholder,
              inlineClassName: 'text-gray-400 opacity-50'
            }
          }
        }
      ]
      decorationIds.current = editorRef.current.deltaDecorations([], decorations)
    }
  }

  const validateContent = (content: string, language: string) => {
    if (language === 'auto') {
      detectLanguage(content)
    } else if (editorRef.current && validationStrategies[language]) {
      const result = validationStrategies[language].validate(content)
      if (result.isValid) {
        // If language is valid but requireLangs is specified, check if this language is allowed
        if (
          requireLangsRef.current.length > 0 &&
          !requireLangsRef.current.includes(language as Language)
        ) {
          const requiredFormats = requireLangsRef.current.map(lang => lang.toUpperCase()).join(', ')
          setValidationError(`Content format not allowed. Must be one of: ${requiredFormats}.`)
          onError()
        } else {
          setValidationError(null)
          errorDecorations.current = editorRef.current.deltaDecorations(
            errorDecorations.current,
            []
          )
          onErrorClear()
        }
      } else {
        setValidationError(result.error || 'Unknown error')
        onError()
        markErrors(result)
      }
    } else {
      // Check if the current language is allowed by requireLangs
      if (
        requireLangsRef.current.length > 0 &&
        !requireLangsRef.current.includes(language as Language)
      ) {
        const requiredFormats = requireLangsRef.current.map(lang => lang.toUpperCase()).join(', ')
        setValidationError(`Content format not allowed. Must be one of: ${requiredFormats}.`)
        onError()
      } else {
        setValidationError(null)
        onErrorClear()
        if (editorRef.current) {
          errorDecorations.current = editorRef.current.deltaDecorations(
            errorDecorations.current,
            []
          )
        }
      }
    }
  }

  const markErrors = (result: ValidationResult) => {
    if (!editorRef.current) return

    const model = editorRef.current.getModel()
    if (!model) return

    const line = result.line || 1
    const column = result.column || 1
    const endColumn = model.getLineMaxColumn(line)

    const newDecorations = [
      {
        range: new monaco.Range(line, column, line, endColumn),
        options: {
          className: 'squiggly-error',
          hoverMessage: { value: result.error || 'Validation Error' },
          inlineClassName: 'error-underline'
        }
      }
    ]

    errorDecorations.current = editorRef.current.deltaDecorations(
      errorDecorations.current,
      newDecorations
    )
  }

  return (
    <div className={cm('monaco-container w-full border border-gray-300', className)}>
      <div ref={containerRef} className='monaco-editor-wrapper h-[450px]'></div>
      {validationError && <ErrorBlock>{validationError}</ErrorBlock>}
    </div>
  )
}
